// About page copy — en / fr / es. EN strings verbatim from the original page.

export const ABOUT_COPY = {
  en: {
    docTitle: "About Bruno Pouliot & Ocean Data — The Quiet Specialist",
    eyebrow: "About · Ocean Data",
    h1a: "The quiet specialist",
    h1b: "behind calmer companies.",
    heroSub:
      "Bruno Pouliot has spent 15+ years helping construction and professional-services companies remove friction from their daily work. His philosophy fits in one sentence:",
    heroQuote: "One company. One truth. Every person gets exactly what they need to do their best work.",
    photoName: "Bruno Pouliot",
    photoCaption: "Founder, Ocean Data · Zoho CRM Certified",
    storyEyebrow: "The story, briefly",
    storyTitle: "He doesn't sell software. He returns Fridays.",
    storyParas: [
      "Bruno's journey started on the basketball courts of Québec — discipline, resilience, teamwork. Then three years in Lyon studying international marketing, where he discovered the gift that still defines his work: making complexity simple.",
      "Through the 2010s he led major accounts at companies like Ricoh Canada and AMJ Campbell. Behind every process he met the same thing — a person weighed down by work about work. The quote chased by phone. The spreadsheet named final_v7. The evening spent making three systems agree.",
      "So in 2017 he founded the practice that became Ocean Data. The method hasn't changed since: start with one critical process, make it flow, measure the capacity it returns — then expand. Today he works between Montreal and Miami, still doing the same thing: giving people their time back.",
    ],
    chips: [
      "Zoho Certified — CRM · Projects · FSM · Books · Creator · Analytics",
      "15+ years",
      "647 Zoho One implementations since 2017",
      "Montreal · Miami",
      "FR · EN · ES · 中文",
    ],
    linkedin: "Follow on LinkedIn →",
    certTitle: "Zoho CRM Certified Administrator",
    certBody:
      "Verified certification — one of six Zoho credentials behind the work. The paperwork matters less than the practice, but you should never have to take either on faith.",
    storiesEyebrow: "Capacity stories",
    storiesTitle: "Not implementations. Hours, handed back.",
    storiesFootnote: "Real outcomes from engagements delivered since 2017.",
    stories: [
      {
        id: "story-construction",
        seat: "Construction · AEC",
        title: "RFI answers in hours, not days",
        body: "Horizon Construction connected their record — in six weeks, RFI response time dropped 43% and decisions stopped waiting on inboxes.",
        returned: "43% faster RFI response · Horizon Construction",
      },
      {
        id: "story-field",
        seat: "Field services",
        title: "Technicians back on customer work",
        body: "A field-service team stopped feeding the office and started serving customers — utilization rose from 47% to 82% once the paperwork moved to the system.",
        returned: "47% → 82% technician utilization",
      },
      {
        id: "story-finance",
        seat: "Finance",
        title: "Payment cycles cut to twelve days",
        body: "With delivered work flowing straight into invoices, the payment cycle shortened from 45 days to 12 — cash arriving while the work is still warm.",
        returned: "45 → 12 days payment cycle",
      },
    ],
    rosterEyebrow: "The teams behind the 647",
    rosterTitle: "Zoho CRM and Zoho One — customized, implemented, and still running.",
    rosterCaption: "A partial roster of the 647 Zoho One implementations delivered since 2017.",
    expertiseEyebrow: "The depth, for those who want it",
    expertiseTitle: "Fifteen years, four disciplines, one quiet engine.",
    expertiseBody: "Open any drawer. The craft is real — it just never asks for your attention.",
    expertise: [
      { q: "Zoho One, end to end", a: "Fifteen years inside the Zoho ecosystem — CRM, Projects, Books, and the 45+ unified apps around them. Bruno designs the whole operating picture, not just the tool in front of you." },
      { q: "Zoho CRM + Projects, deeply", a: "Pipelines, blueprints, milestones, timesheets, workload — configured around how construction and professional-services teams actually deliver, not how the manual reads." },
      { q: "Zoho Books, precisely", a: "Estimates to invoices, retainers, payment reminders, bank reconciliation, project and timesheet billing — set up so the books answer questions instead of raising them." },
      { q: "Claude AI integrations & the Zoho CRM MCP toolset", a: "The quiet engine: 27 purpose-built tools that let Claude read, reason, and act across CRM and Projects in real time — always asking before it writes, always recorded in Zoho's own audit trail." },
    ],
    stickTitle: "Why Bruno's story sticks",
    stick: [
      ["Sticky copy", "He doesn't sell software. He returns Fridays."],
      ["Visual idea", "A desk at 5:01pm, laptop closed, coat on — while the systems keep working."],
      ["15-second film", "Fifteen years compressed into one recurring scene: a different office each year, the same relieved exhale."],
      ["Why it sticks", "A person, not a platform. One promise, repeated and kept: capacity returned, measured, yours."],
    ],
    closeQuote:
      "It's never been about tech for tech's sake. It's about freeing people from the noise of inefficiency — so they can focus on growth, vision, and impact.",
    closeAuthor: "— Bruno Pouliot",
    closeLinks: [
      { label: "Explore the solutions →", to: "/solutions" },
      { label: "Try the live demos →", to: "/demos" },
      { label: "How the 30 days unfold →", to: "/how-it-works" },
    ],
    closeCta: "Talk with Bruno",
    schemaLang: "en-CA",
    hreflangs: { "en-CA": "/about", "fr-CA": "/fr/a-propos", es: "/es/nosotros", "x-default": "/about" },
  },
  fr: {
    docTitle: "À propos de Bruno Pouliot & Ocean Data — Le spécialiste discret",
    eyebrow: "À propos · Ocean Data",
    h1a: "Le spécialiste discret",
    h1b: "derrière les entreprises plus calmes.",
    heroSub:
      "Bruno Pouliot aide depuis plus de 15 ans les entreprises de construction et de services professionnels à enlever la friction de leur quotidien. Sa philosophie tient en une phrase :",
    heroQuote: "Une entreprise. Une vérité. Chaque personne reçoit exactement ce qu'il lui faut pour faire son meilleur travail.",
    photoName: "Bruno Pouliot",
    photoCaption: "Fondateur, Ocean Data · Zoho CRM Certifié",
    storyEyebrow: "L'histoire, brièvement",
    storyTitle: "Il ne vend pas de logiciel. Il rend des vendredis.",
    storyParas: [
      "Le parcours de Bruno a commencé sur les terrains de basketball du Québec — discipline, résilience, esprit d'équipe. Puis trois ans à Lyon à étudier le marketing international, où il a découvert le don qui définit encore son travail : rendre la complexité simple.",
      "Dans les années 2010, il a dirigé des comptes majeurs chez Ricoh Canada et AMJ Campbell. Derrière chaque processus, il rencontrait la même chose — une personne alourdie par le travail à propos du travail. La soumission poursuivie au téléphone. Le chiffrier nommé final_v7. La soirée passée à faire s'accorder trois systèmes.",
      "Alors en 2017, il a fondé la pratique qui est devenue Ocean Data. La méthode n'a pas changé depuis : commencer par un processus critique, le faire couler, mesurer la capacité rendue — puis élargir. Aujourd'hui, il travaille entre Montréal et Miami, faisant toujours la même chose : rendre son temps aux gens.",
    ],
    chips: [
      "Certifié Zoho — CRM · Projects · FSM · Books · Creator · Analytics",
      "15+ ans",
      "647 implémentations Zoho One depuis 2017",
      "Montréal · Miami",
      "FR · EN · ES · 中文",
    ],
    linkedin: "Suivre sur LinkedIn →",
    certTitle: "Administrateur Zoho CRM Certifié",
    certBody:
      "Certification vérifiée — l'une des six accréditations Zoho derrière le travail. Les papiers comptent moins que la pratique, mais vous ne devriez jamais avoir à croire ni l'un ni l'autre sur parole.",
    storiesEyebrow: "Histoires de capacité",
    storiesTitle: "Pas des implantations. Des heures, rendues.",
    storiesFootnote: "Résultats réels de mandats livrés depuis 2017.",
    stories: [
      {
        id: "story-construction",
        seat: "Construction · AEC",
        title: "Des réponses aux RFI en heures, pas en jours",
        body: "Horizon Construction a connecté son dossier — en six semaines, le délai de réponse aux RFI a chuté de 43 % et les décisions ont cessé d'attendre dans les boîtes courriel.",
        returned: "RFI 43 % plus rapides · Construction Horizon",
      },
      {
        id: "story-field",
        seat: "Services sur le terrain",
        title: "Les techniciens de retour au service client",
        body: "Une équipe de services sur le terrain a cessé de nourrir le bureau et a commencé à servir les clients — l'utilisation est passée de 47 % à 82 % quand la paperasse est passée au système.",
        returned: "Utilisation des techniciens : 47 % → 82 %",
      },
      {
        id: "story-finance",
        seat: "Finance",
        title: "Cycle de paiement réduit à douze jours",
        body: "Le travail livré coulant directement dans les factures, le cycle de paiement est passé de 45 à 12 jours — l'argent arrive pendant que le travail est encore chaud.",
        returned: "Cycle de paiement : 45 → 12 jours",
      },
    ],
    rosterEyebrow: "Les équipes derrière les 647",
    rosterTitle: "Zoho CRM et Zoho One — personnalisés, implantés, et toujours en marche.",
    rosterCaption: "Une partie des 647 implémentations Zoho One livrées depuis 2017.",
    expertiseEyebrow: "La profondeur, pour ceux qui la veulent",
    expertiseTitle: "Quinze ans, quatre disciplines, un moteur discret.",
    expertiseBody: "Ouvrez n'importe quel tiroir. Le métier est réel — il ne demande simplement jamais votre attention.",
    expertise: [
      { q: "Zoho One, de bout en bout", a: "Quinze ans dans l'écosystème Zoho — CRM, Projects, Books et les 45+ applications unifiées autour. Bruno conçoit le portrait d'exploitation complet, pas seulement l'outil devant vous." },
      { q: "Zoho CRM + Projects, en profondeur", a: "Pipelines, blueprints, jalons, feuilles de temps, charge de travail — configurés autour de la façon dont les équipes de construction et de services professionnels livrent vraiment, pas de la façon dont le manuel se lit." },
      { q: "Zoho Books, précisément", a: "Des devis aux factures, acomptes, rappels de paiement, rapprochement bancaire, facturation par projet et par feuille de temps — configuré pour que les livres répondent aux questions au lieu d'en soulever." },
      { q: "Intégrations Claude AI et la boîte à outils Zoho CRM MCP", a: "Le moteur discret : 27 outils dédiés qui permettent à Claude de lire, raisonner et agir dans CRM et Projects en temps réel — toujours en demandant avant d'écrire, toujours enregistré dans la piste d'audit de Zoho." },
    ],
    stickTitle: "Pourquoi l'histoire de Bruno colle",
    stick: [
      ["Phrase qui colle", "Il ne vend pas de logiciel. Il rend des vendredis."],
      ["Idée visuelle", "Un bureau à 17h01, portable fermé, manteau sur le dos — pendant que les systèmes continuent de travailler."],
      ["Film de 15 secondes", "Quinze ans compressés en une scène récurrente : un bureau différent chaque année, le même soupir de soulagement."],
      ["Pourquoi ça colle", "Une personne, pas une plateforme. Une promesse, répétée et tenue : capacité rendue, mesurée, à vous."],
    ],
    closeQuote:
      "« Ça n'a jamais été de la technologie pour la technologie. C'est libérer les gens du bruit de l'inefficacité — pour qu'ils se concentrent sur la croissance, la vision et l'impact. »",
    closeAuthor: "— Bruno Pouliot",
    closeLinks: [
      { label: "Explorer les solutions →", to: "/fr/solutions" },
      { label: "Essayer les démos en direct →", to: "/demos" },
      { label: "Le déroulement des 30 jours →", to: "/fr/comment-ca-marche" },
    ],
    closeCta: "Parler avec Bruno",
    schemaLang: "fr-CA",
    hreflangs: { "en-CA": "/about", "fr-CA": "/fr/a-propos", es: "/es/nosotros", "x-default": "/about" },
  },
  es: {
    docTitle: "Acerca de Bruno Pouliot & Ocean Data — El especialista discreto",
    eyebrow: "Acerca de · Ocean Data",
    h1a: "El especialista discreto",
    h1b: "detrás de empresas más tranquilas.",
    heroSub:
      "Bruno Pouliot lleva más de 15 años ayudando a empresas de construcción y servicios profesionales a quitar la fricción de su trabajo diario. Su filosofía cabe en una frase:",
    heroQuote: "Una empresa. Una verdad. Cada persona recibe exactamente lo que necesita para hacer su mejor trabajo.",
    photoName: "Bruno Pouliot",
    photoCaption: "Fundador, Ocean Data · Zoho CRM Certificado",
    storyEyebrow: "La historia, en breve",
    storyTitle: "No vende software. Devuelve viernes.",
    storyParas: [
      "El camino de Bruno empezó en las canchas de baloncesto de Quebec — disciplina, resiliencia, trabajo en equipo. Luego tres años en Lyon estudiando marketing internacional, donde descubrió el don que aún define su trabajo: hacer simple la complejidad.",
      "Durante los años 2010 dirigió cuentas importantes en empresas como Ricoh Canada y AMJ Campbell. Detrás de cada proceso encontraba lo mismo — una persona cargada por el trabajo sobre el trabajo. La cotización perseguida por teléfono. La hoja de cálculo llamada final_v7. La noche pasada haciendo que tres sistemas coincidan.",
      "Así que en 2017 fundó la práctica que se convirtió en Ocean Data. El método no ha cambiado desde entonces: empezar con un proceso crítico, ponerlo a fluir, medir la capacidad devuelta — luego ampliar. Hoy trabaja entre Montreal y Miami, haciendo siempre lo mismo: devolverle su tiempo a la gente.",
    ],
    chips: [
      "Certificado Zoho — CRM · Projects · FSM · Books · Creator · Analytics",
      "15+ años",
      "647 implementaciones de Zoho One desde 2017",
      "Montreal · Miami",
      "FR · EN · ES · 中文",
    ],
    linkedin: "Seguir en LinkedIn →",
    certTitle: "Administrador Zoho CRM Certificado",
    certBody:
      "Certificación verificada — una de las seis credenciales Zoho detrás del trabajo. Los papeles importan menos que la práctica, pero usted nunca debería tener que creer ni lo uno ni lo otro por fe.",
    storiesEyebrow: "Historias de capacidad",
    storiesTitle: "No implementaciones. Horas, devueltas.",
    storiesFootnote: "Resultados reales de proyectos entregados desde 2017.",
    stories: [
      {
        id: "story-construction",
        seat: "Construcción · AEC",
        title: "Respuestas a los RFI en horas, no en días",
        body: "Horizon Construction conectó su expediente — en seis semanas, el tiempo de respuesta a los RFI cayó 43 % y las decisiones dejaron de esperar en los buzones.",
        returned: "RFI 43 % más rápidos · Construction Horizon",
      },
      {
        id: "story-field",
        seat: "Servicios de campo",
        title: "Técnicos de vuelta al trabajo con clientes",
        body: "Un equipo de servicios de campo dejó de alimentar a la oficina y empezó a servir clientes — la utilización subió de 47 % a 82 % cuando el papeleo pasó al sistema.",
        returned: "Utilización de técnicos: 47 % → 82 %",
      },
      {
        id: "story-finance",
        seat: "Finanzas",
        title: "Ciclo de pago reducido a doce días",
        body: "Con el trabajo entregado fluyendo directo a las facturas, el ciclo de pago se acortó de 45 a 12 días — el dinero llega mientras el trabajo aún está tibio.",
        returned: "Ciclo de pago: 45 → 12 días",
      },
    ],
    rosterEyebrow: "Los equipos detrás de las 647",
    rosterTitle: "Zoho CRM y Zoho One — personalizados, implementados, y aún funcionando.",
    rosterCaption: "Una lista parcial de las 647 implementaciones de Zoho One entregadas desde 2017.",
    expertiseEyebrow: "La profundidad, para quien la quiera",
    expertiseTitle: "Quince años, cuatro disciplinas, un motor discreto.",
    expertiseBody: "Abra cualquier cajón. El oficio es real — simplemente nunca pide su atención.",
    expertise: [
      { q: "Zoho One, de punta a punta", a: "Quince años dentro del ecosistema Zoho — CRM, Projects, Books y las 45+ aplicaciones unificadas alrededor. Bruno diseña el cuadro operativo completo, no solo la herramienta frente a usted." },
      { q: "Zoho CRM + Projects, a fondo", a: "Pipelines, blueprints, hitos, hojas de tiempo, carga de trabajo — configurados alrededor de cómo los equipos de construcción y servicios profesionales realmente entregan, no de cómo se lee el manual." },
      { q: "Zoho Books, con precisión", a: "De estimaciones a facturas, anticipos, recordatorios de pago, conciliación bancaria, facturación por proyecto y por horas — configurado para que los libros respondan preguntas en vez de plantearlas." },
      { q: "Integraciones Claude AI y el juego de herramientas Zoho CRM MCP", a: "El motor discreto: 27 herramientas dedicadas que permiten a Claude leer, razonar y actuar en CRM y Projects en tiempo real — siempre pidiendo antes de escribir, siempre registrado en la pista de auditoría de Zoho." },
    ],
    stickTitle: "Por qué la historia de Bruno se queda",
    stick: [
      ["Frase que se pega", "No vende software. Devuelve viernes."],
      ["Idea visual", "Un escritorio a las 5:01pm, portátil cerrado, abrigo puesto — mientras los sistemas siguen trabajando."],
      ["Filme de 15 segundos", "Quince años comprimidos en una escena recurrente: una oficina distinta cada año, el mismo suspiro de alivio."],
      ["Por qué se pega", "Una persona, no una plataforma. Una promesa, repetida y cumplida: capacidad devuelta, medida, suya."],
    ],
    closeQuote:
      "«Nunca se trató de tecnología por la tecnología. Se trata de liberar a la gente del ruido de la ineficiencia — para que pueda enfocarse en el crecimiento, la visión y el impacto.»",
    closeAuthor: "— Bruno Pouliot",
    closeLinks: [
      { label: "Explorar las soluciones →", to: "/es/soluciones" },
      { label: "Probar las demos en vivo →", to: "/demos" },
      { label: "Cómo transcurren los 30 días →", to: "/es/como-funciona" },
    ],
    closeCta: "Hablar con Bruno",
    schemaLang: "es",
    hreflangs: { "en-CA": "/about", "fr-CA": "/fr/a-propos", es: "/es/nosotros", "x-default": "/about" },
  },
};
